<template>
  <div :class="{ '__table-container': true, '__table-container-overlay': isBusy }">
    <b-table
      :id="`POINT_${point.id}`"
      :items="point.activities || []"
      :fields="fields"
      :tbody-tr-class="rowClass"
      class="mc-activity-table"
    >
      <!-- Loading -->
      <template #table-busy>
        <div class="__busy">
          <b-spinner class="align-middle mb-2" />
          <strong>{{ $t('deliveryRequest.table.loading') }}</strong>
        </div>
      </template>

      <!-- Empty Result -->
      <template #empty>
        <div class="text-center my-4">{{ $t('deliveryRequest.table.noData') }}</div>
      </template>

      <template #cell(delivery_order.number)="data">
        <div class="fw-bold mc-title">
          {{ data.value }}
        </div>
      </template>

      <template #cell(movements)="{ item: activity }">
        <div :id="`${activity?.delivery_order?.number}__${activity.type}`" class="__arrow-buttons">
          <button :disabled="!canMove('UP', activity)" @click="handleMoveUp(activity)">
            <i class="icon-fill_arrow-triangle-up"></i>
          </button>

          <button :disabled="!canMove('DOWN', activity)" @click="handleMoveDown(activity)">
            <i class="icon-fill_arrow-triangle-down"></i>
          </button>
        </div>
      </template>

      <template #cell(activity_type)="{ item: { type } }">
        <div v-if="type == 'PICKUP'" class="__activity_badge __pickup">{{ $t('deliveryRequest.activity.pickup') }}</div>
        <div v-if="type == 'DROP'" class="__activity_badge __drop">{{ $t('deliveryRequest.activity.delivery') }}</div>
      </template>

      <template #cell(completed_on)="{ value }">
        <span>{{ formatDateWithTz(value) }}</span>
      </template>

      <template #cell(delivery_order.total_weight)="{ value }">
        <span>{{ value | formatNumber }} Kg</span>
      </template>

      <template #cell(delivery_order.total_volume)="{ value }">
        <span>{{ value | formatNumber }} Cbm</span>
      </template>

      <template #cell(remove)="{ item }">
        <div class="__remove-order-container">
          <div
            role="button"
            class="__remove-order-button"
            @click="() => handleClickItem('remove', item)"
          >
            <i class="icon-outline_trash-2"></i>
            <span>{{ $t('deliveryRequest.actions.removeDelivery') }}</span>
          </div>
        </div>
      </template>

      <template #cell(status)="{ item }">
        <template v-if="item.completed_on">
          <span class="__status-dots __completed"></span>
          <span>{{ $t('deliveryRequest.status.completed') }}</span>
        </template>
        <template v-else>
          <span class="__status-dots __not-completed"></span>
          <span>{{ $t('deliveryRequest.status.notCompleted') }}</span>
        </template>
      </template>

      <template #cell(address)>
        {{ point.address.name }}
      </template>

      <template #cell(target_pickup_delivery)="{ item }">
        {{ getTarget(item) }}
      </template>

      <template #custom-foot="{ columns, items }">
        <b-tr class="__activity-footer">
          <b-th />

          <b-th :colspan="columns - 3">
            <td>{{ $t('deliveryRequest.table.total') }}</td>
          </b-th>

          <b-th>
            <td>{{ sum('total_weight', items) | formatNumber }} Kg</td>
          </b-th>

          <b-th>
            <td>{{ sum('total_volume', items) | formatNumber }} Cbm</td>
          </b-th>

          <b-th />
        </b-tr>
      </template>
    </b-table>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { sum } from '@/helpers/calculate'

import * as FleetTaskActivity from '@/services/rest-api/lib/fleet-task/activity'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'

import { fields } from './fields.js'

import moment from 'moment'
import { formatDateWithTz, formatIndexDate } from '@/helpers/tzDate'
moment.locale('id')

export default {
  props: {
    point: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isBusy: false,
      pendingRow: null,

      selectedActivity: {},
    }
  },
  computed: {
    ...mapState('fleetTask', ['fleetTaskStatus', 'selectedDos']),
    fields() {
      return fields(this.$t.bind(this))
    },
  },
  methods: {
    formatDateWithTz,
    formatIndexDate,
    // TODO: change sum to helper
    sum(key, items) {
      return items.reduce((acc, { delivery_order }) => sum(acc, delivery_order[key]), 0)
    },
    async handleMoveUp(activity) {
      const res = await this.handleMove(activity, 'UP')
      if (res?.errors) return

      EventBus.$emit('refetch-sequence')
    },
    async handleMoveDown(activity) {
      const res = await this.handleMove(activity, 'DOWN')
      if (res?.errors) return

      EventBus.$emit('refetch-sequence')
    },
    async handleMove(activity, direction) {
      this.isBusy = true

      const hash = `#${activity?.delivery_order?.number}__${activity.type}`
      if (this.$route.hash !== hash) this.$router.replace({ hash })

      return FleetTaskActivity.move(
        this.$route.params.id,
        this.point.id,
        activity.id,
        direction
      ).then(() => (this.isBusy = false))
    },
    canMove(direction, activity) {
      const idx = (activity?.movements || []).findIndex(
        (v) => v.toUpperCase() == direction.toUpperCase()
      )
      return idx != -1
    },
    async handleClickItem(type, item) {
      this.selectedActivity = { ...item }
      await this.$nextTick()

      if (type == 'remove') {
        this.$bvModal.show(
          `mc-modal-remove-order-${this.selectedActivity?.delivery_order.id}-${this.point.id}`
        )
      } else if (type == 'complete') {
        this.$refs['complete-activity-modal'].HandleCompleteActivity()
      } else if (type == 'transfer') {
        this.$bvModal.show(
          `mc-modal-transfer-${this.selectedActivity?.delivery_order.id}-${this.point.id}`
        )
      }
    },
    rowClass(item, type) {
      if (!item || type !== 'row') return

      const hash = `#${item?.delivery_order?.number}__${item.type}`
      if (this.$route.hash == hash) {
        return '__highlight-row'
      }
    },
    getTarget(item) {
      const order = item.delivery_order
      if (!order) {
        return '-'
      }

      let result = '-'
      if (item.type === 'PICKUP') {
        if (order.expected_pickup_on) {
          const formatted = formatIndexDate(order.expected_pickup_on, 'DD MMM YYYY')
          result = formatted.date ? `${formatted.date} ${formatted.time}` : '-'
        }
      } else {
        if (order.expected_delivered_on) {
          const formatted = formatIndexDate(order.expected_delivered_on, 'DD MMM YYYY')
          result = formatted.date ? `${formatted.date} ${formatted.time}` : '-'
        }
      }

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.__activity-footer > th {
  background: #ebecef;
  padding: 0;

  & td {
    padding: 12.5px 12px;
    background-color: transparent;
  }
}

:deep(.mc-activity-table) {
  margin-top: 10px;
  text-align: left;

  .th-movements {
    width: 30px;
  }

  .th-activity {
    width: 50px;
  }

  .th-shipment {
    width: 130px;
  }

  .th-customer {
    width: 500px;
  }

  .th-weight-volume,
  .th-reference
  .th-reference-internal {
    width: 130px;
  }

  .th-remove {
    width: 175px;
  }

  .th-do {
    width: 130px;
  }

  td {
    vertical-align: middle;
  }

  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .__completed {
    background-color: #429677;
  }

  .__not-completed {
    background-color: #99a1b1;
  }
}

.__remove-order-button {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  width: 20px;
  cursor: pointer;

  color: #f41d00;
  border: 1px solid #f41d00;
  padding: 2px;
  border-radius: 3px;

  transition: all 0.2s ease-in-out;

  i::before {
    font-size: 16px;
    color: #f41d00;
  }

  span {
    white-space: nowrap;
    font-size: 11px;
    padding-left: 4px;
    display: none;
    overflow: hidden;
  }

  &:hover {
    width: 150px;

    span {
      display: inline-block;
    }
  }
}

.__activity_badge {
  padding: 4px 8px;
  color: white;
  border-radius: 3px;
  font-weight: 600;
  font-size: 11px;
  width: fit-content;
  line-height: 16px;

  &.__pickup {
    background: #1c74da;
  }

  &.__drop {
    background: #137c55;
  }
}

.__arrow-buttons {
  line-height: 8px;

  button {
    padding: 0;
    border: 0;
    background: none;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    i::before {
      font-size: 18px;
    }
  }
}

.__table-container {
  overflow-x: auto;
  position: relative;
  margin-bottom: 20px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.__table-container-overlay::after {
  content: ' ';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.5);
  z-index: 10;
}

:deep(.__highlight-row) {
  animation: blinker 0.5s linear 3;
  outline: 1px solid rgba($primary, 0.3);
  outline-offset: -1px;
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>
